import { takeEvery, put, call, all, select } from "redux-saga/effects";
import apiCall from "../services"; // Assuming apiCall service is correctly set up

const apiUrl = process.env.REACT_APP_API_URL;
const enviromentUrl = process.env.REACT_APP_ENVIROMENT;

// --- Action Types ---
const PROCESS_PRODUCT_STEP = "PROCESS_PRODUCT_STEP";
const PRODUCT_STEP_PENDING = "PRODUCT_STEP_PENDING";
const PRODUCT_STEP_SUCCESS = "PRODUCT_STEP_SUCCESS";
const PRODUCT_STEP_FAILURE = "PRODUCT_STEP_FAILURE";

// --- Helper Functions (copied/adapted from other sagas) ---

const getSetCookie = () => {
    // Ensure this function correctly retrieves the UUID needed for headers
    // Consider passing it via action payload if it can change or is sensitive
    return window.localStorage.getItem("uuidSetCookie");
};

const getAuthToken = () => {
    // Ensure this function correctly retrieves the auth token
    // Consider passing it via action payload
    return window.localStorage.getItem("headersSetCookie"); // Assuming this stores the Bearer token
};


function* callCreateProductImage(productUuid, imagesData) {
    const access = getAuthToken();
    const realmUuid = getSetCookie();

    if (!imagesData) {
        return { responseStatus: "success", type: 'CREATE_PRODUCT_IMAGE', message: 'No image data received.' };
    }

    if (imagesData.length === 0) {
        // Cannot proceed with deletion logic as the type is unknown.
        return { responseStatus: "success", type: 'CREATE_PRODUCT_IMAGE', message: 'No new images to process.' };
    }

    const imageType = imagesData[0].type;

    let baseUrlGet = `${apiUrl}/opa-product-image/${productUuid}`;
    let baseUrlPostDelete = `${apiUrl}/opa-product-images/${productUuid}`;

    if (enviromentUrl) {
        baseUrlGet += baseUrlGet.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
        baseUrlPostDelete += baseUrlPostDelete.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
    }

    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": realmUuid,
        };

        // 1. Get all current images for the product
        const getRequestOptions = { headers };
        const allCurrentImagesResponse = yield call(apiCall, "GET", baseUrlGet, getRequestOptions);
        const allCurrentImages = Array.isArray(allCurrentImagesResponse) ? allCurrentImagesResponse : (allCurrentImagesResponse?.records || []);

        // Filter by the type we are currently processing
        const currentImagesOfType = allCurrentImages.filter(img => img.type === imageType);
        const currentImageUuids = currentImagesOfType.map(img => img.uuid);

        // 2. Compare current with new to find what to add and what to delete
        const newImageUuids = imagesData.map(img => img.imageUuid).filter(uuid => uuid !== null);

        const imagesToAdd = imagesData.filter(img => img.imageUuid === null);
        const imageUuidsToDelete = currentImageUuids.filter(uuid => !newImageUuids.includes(uuid));

        const imagesToUpdate = imagesData.filter((img) => {
            if (!img.imageUuid) return false;
            const orig = img.imageUuid;
            if (!orig) return false;
            return (img.alt !== orig.alt) || (img.type !== orig.type) || (img.sortOrder !== orig.sortOrder);
        }).map((img) => ({ imageUuid: img.imageUuid, alt: img.alt, type: img.type, sortOrder: img.sortOrder }));

        const apiCalls = [];

        // 3. Prepare POST for new images
        if (imagesToAdd.length > 0) {
            const addRequestOptions = {
                headers,
                body: JSON.stringify(imagesToAdd),
            };
            apiCalls.push(call(apiCall, "POST", baseUrlPostDelete, addRequestOptions));
        }

        // 4. Prepare DELETE for removed images
        if (imageUuidsToDelete.length > 0) {
            const deleteRequestOptions = {
                headers,
                body: JSON.stringify(imageUuidsToDelete),
            };
            apiCalls.push(call(apiCall, "DELETE", baseUrlPostDelete, deleteRequestOptions));
        }

        // 5. Prepare PATCH for updated images
        const galleryImages = imagesToUpdate.filter(
            image => image.type === "GALLERY"
        );

        galleryImages.forEach(image => {
            let baseUrlPatch = `${apiUrl}/opa-product-image/${productUuid}/${image.imageUuid}`;

            if (enviromentUrl) {
                baseUrlPatch += baseUrlPatch.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
            }

            const addRequestOptions = {
                headers,
                body: JSON.stringify(image),
            };

            apiCalls.push(
                call(apiCall, "PATCH", baseUrlPatch, addRequestOptions)
            );
        });

        // 6. Execute all necessary API calls in parallel
        if (apiCalls.length > 0) {
            yield all(apiCalls);
        } else {

        }

        return { responseStatus: "success", type: 'CREATE_PRODUCT_IMAGE', message: 'Images updated successfully.' };

    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update product images';
        return {
            responseStatus: "error",
            message: errorMessage,
            type: 'CREATE_PRODUCT_IMAGE'
        };
    }
}

function* callAttachProductPricing(productUuid, data) {
    const access = getAuthToken();
    const realmUuid = getSetCookie();

    if (!Array.isArray(data) || data.length === 0) {
        return { responseStatus: "success", message: "No pricing schedules to attach or data format incorrect.", type: 'ATTACH_PRODUCT_PRICING' };
    }

    let baseUrl = `${apiUrl}/opa-product-pricing/${productUuid}`;
    if (enviromentUrl) {
        baseUrl += baseUrl.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
    }

    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": realmUuid,
        };

        // Create an array of 'call' effects for each schedule
        const apiCalls = data.map(schedule => {
            const requestOptions = {
                headers: headers,
                body: JSON.stringify(schedule) // Send each schedule object individually
            };
            return call(apiCall, "POST", baseUrl, requestOptions);
        });

        // Execute all calls in parallel
        const responses = yield all(apiCalls);

        // Check if all responses were successful
        // Assuming response[0].updatedAt indicates success for each individual call
        const allSuccessful = responses.every(response => response && response[0] && response[0].updatedAt);

        if (allSuccessful) {
            return {
                data: responses.map(r => r[0]), // Array of successful response data
                responseStatus: "success",
                type: 'ATTACH_PRODUCT_PRICING'
            };
        } else {
            // If one or more calls failed (but didn't throw an exception caught by the main catch block)
            const failedResponseDetails = responses.find(response => !(response && response[0] && response[0].updatedAt));

            return {
                message: 'Failed to attach one or more product pricing schedules. Please check details.',
                responseStatus: "error",
                type: 'ATTACH_PRODUCT_PRICING',
                errorDetails: failedResponseDetails // Optional: provide some detail from the first non-successful response
            };
        }

    } catch (error) { // This catches errors if any of the 'call' effects in 'all' fails (e.g. network error)

        // Attempt to provide a more specific error message if available from the error object
        const errorMessage = error.response?.data?.message || error.message || 'Failed to addd product pricing schedules.';
        return {
            // ...error, // Spreading the original error object might be too verbose or expose too much
            responseStatus: "error",
            message: errorMessage,
            type: 'ATTACH_PRODUCT_PRICING'
        };
    }
}

function* callAttachProductTaxesAndFees(productUuid, taxesAndFees) {
    const access = getAuthToken();
    const realmUuid = getSetCookie();
    // This is the desired state of tax UUIDs for the product
    const newTaxUuids = taxesAndFees || [];

    let baseUrl = `${apiUrl}/opa-product-taxes/${productUuid}`;
    let baseUrlGet = `${apiUrl}/opa-product-tax/${productUuid}`;
    if (enviromentUrl) {
        baseUrl += baseUrl.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
        baseUrlGet += baseUrlGet.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
    }

    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": realmUuid,
        };

        // 1. Get current taxes associated with the product
        const getRequestOptions = { headers };
        const currentTaxesResponse = yield call(apiCall, "GET", baseUrlGet, getRequestOptions);

        // Handle if API returns an array directly, or an object with a 'records' property
        const currentTaxRecords = Array.isArray(currentTaxesResponse) ? currentTaxesResponse : (currentTaxesResponse?.records || []);
        const currentTaxUuids = currentTaxRecords.map(tax => tax.taxUuid);


        // 2. Compare current with new to find what to add and what to delete
        const taxesToAdd = newTaxUuids.filter(uuid => !currentTaxUuids.includes(uuid));
        const taxesToDelete = currentTaxUuids.filter(uuid => !newTaxUuids.includes(uuid));

        const apiCalls = [];

        // 3. If there are taxes to add, prepare a POST request with an array of UUIDs.
        if (taxesToAdd.length > 0) {
            const addRequestOptions = {
                headers: headers,
                body: JSON.stringify(taxesToAdd),
            };
            apiCalls.push(call(apiCall, "POST", baseUrl, addRequestOptions));
        }

        // 4. If there are taxes to delete, prepare a DELETE request with an array of UUIDs in the body.
        if (taxesToDelete.length > 0) {
            const deleteRequestOptions = {
                headers: headers,
                body: JSON.stringify(taxesToDelete),
            };
            apiCalls.push(call(apiCall, "DELETE", baseUrl, deleteRequestOptions));
        }

        // 5. Execute all necessary API calls in parallel
        if (apiCalls.length > 0) {
            const responses = yield all(apiCalls);
        } else {

        }

        // 6. If all calls succeed without throwing an error, return a success status
        return { responseStatus: "success", type: 'ATTACH_PRODUCT_TAXES_AND_FEES', message: 'Taxes and fees updated successfully.' };

    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update product taxes and fees';
        return {
            responseStatus: "error",
            message: errorMessage,
            type: 'ATTACH_PRODUCT_TAXES_AND_FEES'
        };
    }
}

function* callAttachProductBlackouts(productUuid, blackouts) {
    const access = getAuthToken();
    const realmUuid = getSetCookie();
    // This is the desired state of blackout UUIDs for the product
    const newBlackoutUuids = blackouts || [];


    const baseUrlGet = `${apiUrl}/opa-product-blackout-date/${productUuid}`;
    const baseUrlPostDelete = `${apiUrl}/opa-product-blackout-dates/${productUuid}`;

    const getUrl = enviromentUrl ? (baseUrlGet.includes('?') ? `${baseUrlGet}&env=${enviromentUrl}` : `${baseUrlGet}?env=${enviromentUrl}`) : baseUrlGet;
    const postDeleteUrl = enviromentUrl ? (baseUrlPostDelete.includes('?') ? `${baseUrlPostDelete}&env=${enviromentUrl}` : `${baseUrlPostDelete}?env=${enviromentUrl}`) : baseUrlPostDelete;


    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": realmUuid,
        };

        // 1. Get current blackouts associated with the product
        const getRequestOptions = { headers };
        const currentBlackoutsResponse = yield call(apiCall, "GET", getUrl, getRequestOptions);
        const currentBlackoutRecords = Array.isArray(currentBlackoutsResponse) ? currentBlackoutsResponse : (currentBlackoutsResponse?.records || []);
        const currentBlackoutUuids = currentBlackoutRecords.map(b => b.blackoutDateUuid);

        // 2. Compare current with new to find what to add and what to delete
        const blackoutsToAdd = newBlackoutUuids.filter(uuid => !currentBlackoutUuids.includes(uuid));
        const blackoutsToDelete = currentBlackoutUuids.filter(uuid => !newBlackoutUuids.includes(uuid));

        const apiCalls = [];

        // 3. If there are blackouts to add, prepare a POST request.
        if (blackoutsToAdd.length > 0) {
            const addRequestOptions = {
                headers: headers,
                body: JSON.stringify(blackoutsToAdd),
            };
            apiCalls.push(call(apiCall, "POST", postDeleteUrl, addRequestOptions));
        }

        // 4. If there are blackouts to delete, prepare a DELETE request.
        if (blackoutsToDelete.length > 0) {
            const deleteRequestOptions = {
                headers: headers,
                body: JSON.stringify(blackoutsToDelete),
            };
            apiCalls.push(call(apiCall, "DELETE", postDeleteUrl, deleteRequestOptions));
        }

        // 5. Execute all necessary API calls in parallel
        if (apiCalls.length > 0) {
            yield all(apiCalls);
        } else {
        }

        // 6. If all calls succeed, return a success status
        return { responseStatus: "success", type: 'ATTACH_PRODUCT_BLACKOUTS', message: 'Blackouts updated successfully.' };

    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update product blackouts';
        return {
            responseStatus: "error",
            message: errorMessage,
            type: 'ATTACH_PRODUCT_BLACKOUTS'
        };
    }
}

function* callAttachProductPool(productUuid, newPoolUuid) {
    const access = getAuthToken();
    const realmUuid = getSetCookie();

    const baseUrlGet = `${apiUrl}/opa-product/${productUuid}?include=pool`;
    const baseUrlPostDelete = `${apiUrl}/opa-product-pools/${productUuid}`;

    const getUrl = enviromentUrl
        ? (baseUrlGet.includes('?') ? `${baseUrlGet}&env=${enviromentUrl}` : `${baseUrlGet}?env=${enviromentUrl}`)
        : baseUrlGet;
    const postDeleteUrl = enviromentUrl
        ? (baseUrlPostDelete.includes('?') ? `${baseUrlPostDelete}&env=${enviromentUrl}` : `${baseUrlPostDelete}?env=${enviromentUrl}`)
        : baseUrlPostDelete;

    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": realmUuid,
        };

        const getRequestOptions = { headers };
        const currentPoolsResponse = yield call(apiCall, "GET", getUrl, getRequestOptions);

        const currentPools = Array.isArray(currentPoolsResponse)
            ? currentPoolsResponse
            : (currentPoolsResponse?.pools || []);

        const currentPoolUuid = currentPools.length > 0 ? currentPools[0].poolUuid : null;

        if (currentPoolUuid && currentPoolUuid !== newPoolUuid) {
            const deleteRequestOptions = {
                headers,
                body: JSON.stringify([currentPoolUuid]),
            };
            yield call(apiCall, "DELETE", postDeleteUrl, deleteRequestOptions);
        }

        if (newPoolUuid && currentPoolUuid !== newPoolUuid) {
            const addRequestOptions = {
                headers,
                body: JSON.stringify([newPoolUuid]),
            };
            yield call(apiCall, "POST", postDeleteUrl, addRequestOptions);
        }

        return {
            responseStatus: "success",
            type: 'ATTACH_PRODUCT_POOL',
            message: 'Pool updated successfully.'
        };

    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update product Pool';
        return {
            responseStatus: "error",
            message: errorMessage,
            type: 'ATTACH_PRODUCT_POOL'
        };
    }
}


function* callCreateProduct(data) {
    const access = getAuthToken();
    const uuid = getSetCookie();

    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": uuid,
        };
        const requestOptions = {
            headers: headers,
            body: JSON.stringify(data),
        };
        let url = `${apiUrl}/opa-product`;
        if (enviromentUrl) {
            url += `?env=${enviromentUrl}`;
        }
        const response = yield call(apiCall, "POST", url, requestOptions);

        // Add a status for easier checking later
        let responseStatus = response?.createdAt ? "success" : "error";
        return { ...response, responseStatus, type: 'CREATE_PRODUCT' }; // Add type for identification

    } catch (error) {
        return { ...error, responseStatus: "error", message: error.message || 'Failed to create product', type: 'CREATE_PRODUCT' };
    }
}

function* callUpdateProduct(productUuid, data) {
    const access = getAuthToken();
    const realmUuid = getSetCookie();


    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": realmUuid,
        };
        const requestOptions = {
            headers: headers,
            body: JSON.stringify(data),
        };
        let url = `${apiUrl}/opa-product/${productUuid}`;
        if (enviromentUrl) {
            url += url.includes('?') ? `&env=${enviromentUrl}` : `?env=${enviromentUrl}`;
        }
        const response = yield call(apiCall, "PATCH", url, requestOptions);

        let responseStatus = response?.updatedAt ? "success" : "error";
        return { ...response, responseStatus, type: 'UPDATE_PRODUCT' };

    } catch (error) {
        return { ...error, responseStatus: "error", message: error.message || 'Failed to update product', type: 'UPDATE_PRODUCT' };
    }
}

function* callGetProductCategories() {
    const access = getAuthToken();
    const uuid = getSetCookie();


    try {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${access}`,
            "X-RealmUuid": uuid,
        };
        const requestOptions = { headers: headers };
        let url = `${apiUrl}/opa-product-categories?page_size=1000`; // Assuming default page size is okay
        if (enviromentUrl) {
            url += `&env=${enviromentUrl}`;
        }
        const response = yield call(apiCall, "get", url, requestOptions);

        // Basic check for success (e.g., presence of records array)
        let responseStatus = Array.isArray(response?.records) ? "success" : "error";
        return { ...response, responseStatus, type: 'GET_PRODUCT_CATEGORIES' };

    } catch (error) {

        return { ...error, responseStatus: "error", message: error.message || 'Failed to get product categories', type: 'GET_PRODUCT_CATEGORIES' };
    }
}

// --- Orchestration Saga ---

function* processProductStepSaga(action) {
    const { transitionKey, actionsConfig, data, productUuid: initialUuid } = action.payload;

    // 1. Dispatch Pending state
    yield put({ type: PRODUCT_STEP_PENDING, payload: { transitionKey } });

    let currentProductUuid = initialUuid;
    const results = [];
    let overallSuccess = true;
    let finalErrorMessage = null;
    let newProductUuid = null;

    try {
        // 2. Execute actions sequentially
        for (const apiAction of actionsConfig) {
            let result;
            const preparedData = apiAction.prepareData ? apiAction.prepareData(data, currentProductUuid) : null;

            switch (apiAction.type) {
                case "CREATE_PRODUCT": // Changed from CREATE_PRODUCTS for clarity
                    result = yield call(callCreateProduct, preparedData.data); // Assuming prepareData structure
                    if (result.responseStatus === "success" && result.uuid) {
                        currentProductUuid = result.uuid; // Capture the new UUID
                        newProductUuid = result.uuid; // Store to pass back on final success
                    }
                    break;
                case "UPDATE_PRODUCT":
                    if (!currentProductUuid) {
                        throw new Error("Cannot UPDATE_PRODUCT without a product UUID.");
                    }
                    result = yield call(callUpdateProduct, currentProductUuid, preparedData.data);
                    break;
                case "CREATE_PRODUCT_IMAGE":
                    if (!currentProductUuid) {
                        throw new Error("Cannot CREATE_PRODUCT_IMAGE without a product UUID.");
                    }
                    result = yield call(callCreateProductImage, currentProductUuid, preparedData.data);
                    break;
                case "ATTACH_PRODUCT_PRICING":
                    if (!currentProductUuid) {
                        throw new Error("Cannot ATTACH_PRODUCT_PRICING without a product UUID.");
                    }
                    result = yield call(callAttachProductPricing, currentProductUuid, preparedData.data);
                    break;
                case "ATTACH_PRODUCT_TAXES_AND_FEES":
                    if (!currentProductUuid) {
                        throw new Error("Cannot ATTACH_PRODUCT_TAXES_AND_FEES without a product UUID.");
                    }
                    result = yield call(callAttachProductTaxesAndFees, currentProductUuid, preparedData.data);
                    break;
                case "ATTACH_PRODUCT_BLACKOUTS":
                    if (!currentProductUuid) {
                        throw new Error("Cannot ATTACH_PRODUCT_BLACKOUTS without a product UUID.");
                    }
                    result = yield call(callAttachProductBlackouts, currentProductUuid, preparedData.data);
                    break;
                case "ATTACH_PRODUCT_POOL":
                    if (!currentProductUuid) {
                        throw new Error("Cannot ATTACH_PRODUCT_POOL without a product UUID.");
                    }
                    result = yield call(callAttachProductPool, currentProductUuid, preparedData.data);
                    break;
                default:
                    // console.warn(`Unsupported action type in workflow: ${apiAction.type}`);
                    result = { responseStatus: "warning", message: `Unsupported action: ${apiAction.type}`, type: apiAction.type };
                    break;
            }

            results.push(result); // Store result regardless of success/failure

            // 3. Check if required action failed
            if (apiAction.requireSuccess && result.responseStatus !== "success") {
                overallSuccess = false;
                finalErrorMessage = `Failed during ${result.type}: ${result.message || 'See console for details.'}`;
                break; // Stop processing further actions
            }
        }

        // 4. Dispatch final status
        if (overallSuccess) {
            yield put({ type: PRODUCT_STEP_SUCCESS, payload: { transitionKey, results, newProductUuid } });
        } else {
            // Error already logged above where it happened
            yield put({ type: PRODUCT_STEP_FAILURE, payload: { transitionKey, error: finalErrorMessage, results } });
        }

    } catch (error) {
        // Catch unexpected errors during the saga execution itself
        // console.error('%c ((SAGA WORKFLOW)) / Uncaught Error in processProductStepSaga:', 'color:rgb(250, 194, 157); background:rgb(150, 67, 12)', error);
        results.push({ responseStatus: "error", message: error.message, type: 'SAGA_ERROR' }); // Add saga error to results
        yield put({ type: PRODUCT_STEP_FAILURE, payload: { transitionKey, error: error.message || "An unexpected error occurred in the workflow.", results } });
    }
}

// --- Watcher Saga ---
export function* watchProductWorkflow() {
    yield takeEvery(PROCESS_PRODUCT_STEP, processProductStepSaga);
}